/**
 * sourcing/render-modals.ts — 모달 렌더 (니즈 생성 / 매입처 지정)
 *
 * SourcingView.ts 에서 분리 (M4, 2026-07-08 — deal-detail 분해 계획 v2 패턴 이식).
 * 이동만 — 동작·마크업 변화 없음. 전부 "state → HTML 문자열" 순수 함수.
 */

import * as api from '../../api/client';
import { escapeHtml, catalogHitDescription } from '../../lib/cells';
import { CONDITION_OPTIONS, type CreateState, type SupplierModalState, type VisibilityModalState } from './types';

// ── 니즈 생성 — 품목 검색 결과(딜 상세 니즈검색과 동일 규칙/클래스 재사용) ──
export function renderCreateSearchResultsHtml(c: CreateState): string {
  if (c.searching) return '<div class="deal-item-search-empty">검색 중…</div>';
  if (c.query.trim().length < 2) return '<div class="deal-item-search-empty">2글자 이상 입력해 주세요.</div>';
  if (c.hits.length === 0) return '<div class="deal-item-search-empty">일치하는 품목이 없습니다.</div>';
  return c.hits.map((hit) => {
    const key  = `${hit.source}::${hit.option_part}`;
    const desc = catalogHitDescription(hit);
    return `<button class="deal-item-search-row" type="button" data-action="create-pick" data-key="${escapeHtml(key)}" title="더블클릭으로 선택">
              <code class="deal-item-search-part">${escapeHtml(hit.option_part)}</code>
              <span class="deal-item-search-desc" title="${escapeHtml(desc)}">${escapeHtml(desc)}</span>
            </button>`;
  }).join('');
}

// ── 니즈 생성 모달 ──
export function renderCreateModal(c: CreateState): string {
  if (!c.open) return '';
  const condOpts = ['<option value="">컨디션 선택</option>',
    ...CONDITION_OPTIONS.map((o) => `<option value="${escapeHtml(o)}" ${o === c.condition ? 'selected' : ''}>${escapeHtml(o)}</option>`),
  ].join('');
  const tagBtns = (arr: string[], picked: string[], kind: string) =>
    arr.map((t) => `<button type="button" class="sourcing-tag-pick ${picked.includes(t) ? 'is-on' : ''}"
               data-action="create-tag" data-kind="${kind}" data-tag="${escapeHtml(t)}">${escapeHtml(t)}</button>`).join(' ');
  return `
      <div class="deal-quote-modal" role="dialog">
        <div class="deal-quote-modal-overlay" data-action="create-close"></div>
        <div class="deal-quote-modal-dialog" style="max-width:720px">
          <header class="deal-quote-modal-header">
            <h2>니즈 생성</h2>
            <button class="deal-rfq-modal-close" type="button" data-action="create-close" aria-label="닫기">✕</button>
          </header>
          <div class="deal-quote-modal-body sourcing-create-body">
            ${c.error ? `<div class="alert alert-error">${escapeHtml(c.error)}</div>` : ''}
            <div class="sourcing-create-search">
              <span class="sourcing-create-search-label">품목 검색</span>
              ${c.optionPart || c.itemText
                ? `<div class="deal-need-picked">
                     ${c.optionPart ? `<code>${escapeHtml(c.optionPart)}</code>` : ''}
                     <span class="deal-need-picked-desc">${escapeHtml(c.itemText || '-')}</span>
                     <button class="btn btn-ghost btn-sm" type="button" data-action="create-clear">변경</button>
                   </div>`
                : `<input type="text" class="input deal-item-search-input" data-action="create-search"
                          value="${escapeHtml(c.query)}" placeholder="파트넘버 또는 디스크립션 검색 (2글자 이상)" autocomplete="off" />
                   <div class="deal-item-search-results" id="sourcing-need-search-results">
                     ${renderCreateSearchResultsHtml(c)}
                   </div>`}
            </div>
            <div class="sourcing-create-row">
              <label>수량<input class="input" type="number" min="1" data-action="create-field" data-field="quantity" value="${escapeHtml(c.quantity)}"></label>
              <label>컨디션<select class="input" data-action="create-field" data-field="condition">${condOpts}</select></label>
            </div>
            <div class="sourcing-create-tags">
              <div><span class="sourcing-create-tag-label">영업 요청</span> ${tagBtns([...api.SALES_REQUEST_TAGS], c.salesTags, 'sales')}</div>
              <div><span class="sourcing-create-tag-label">소싱 메모</span> ${tagBtns([...api.SOURCING_MEMO_TAGS], c.memoTags, 'memo')}</div>
            </div>
          </div>
          <footer class="deal-quote-modal-footer">
            <div class="deal-quote-modal-actions">
              <button class="btn btn-primary" type="button" data-action="create-submit" ${c.saving ? 'disabled' : ''}>${c.saving ? '생성 중…' : '생성'}</button>
            </div>
          </footer>
        </div>
      </div>`;
}

// ── 매입처 지정 모달 ──
// 매입처 리스트 내부(검색 필터 + 선택 상단고정/순서 + 행). 검색 시 이 부분만 갱신(포커스 유지).
export function renderSupplierListInner(m: SupplierModalState): string {
  const q = m.search.trim().toLowerCase();
  const filtered = q
    ? m.all.filter((s) =>
        s.name.toLowerCase().includes(q) ||
        (s.country ?? '').toLowerCase().includes(q) ||
        s.categories.some((c) => c.toLowerCase().includes(q)))
    : m.all;
  // 선택된 매입처를 위로(선택 순서), 나머지는 백엔드 정렬(가격수신 DESC) 유지.
  const order   = new Map(m.selected.map((id, i) => [id, i]));
  const baseIdx = new Map(filtered.map((s, i) => [String(s.id), i]));
  const sorted = [...filtered].sort((a, b) => {
    const ai = order.has(String(a.id)), bi = order.has(String(b.id));
    if (ai !== bi) return ai ? -1 : 1;
    if (ai && bi) return order.get(String(a.id))! - order.get(String(b.id))!;
    return baseIdx.get(String(a.id))! - baseIdx.get(String(b.id))!;
  });

  const rows = sorted.map((s) => {
    const id = String(s.id);
    const on = m.selected.includes(id);
    const meta = [s.country, s.categories.join(', ')].filter(Boolean).join(' · ');
    const badge = s.priced_rfq > 0
      ? `<span class="sourcing-supplier-rfq is-priced" title="가격 수신 ${s.priced_rfq}건">가격 ${s.priced_rfq}</span>`
      : s.total_rfq > 0
        ? `<span class="sourcing-supplier-rfq" title="RFQ 문의 ${s.total_rfq}건">문의 ${s.total_rfq}</span>`
        : '';
    // 선택된(체크) 행은 드래그 핸들로 순서 변경 가능(§5.5.8). 미선택은 핸들 자리만.
    const handle = on
      ? `<span class="sourcing-supplier-drag" draggable="true" data-action="supplier-drag" data-supplier-id="${escapeHtml(id)}" title="순서 변경">⋮⋮</span>`
      : `<span class="sourcing-supplier-drag-sp" aria-hidden="true"></span>`;
    return `
        <div class="sourcing-supplier-row ${on ? 'is-on' : ''}"${on ? ` data-action="supplier-drop" data-supplier-id="${escapeHtml(id)}"` : ''}>
          ${handle}
          <button class="sourcing-supplier-pick ${on ? 'is-on' : ''}" type="button"
                  data-action="supplier-toggle" data-supplier-id="${escapeHtml(id)}">
            <span class="sourcing-supplier-check" aria-hidden="true">${on ? '☑' : '☐'}</span>
            <span class="sourcing-supplier-name">${escapeHtml(s.name)}</span>
            ${meta ? `<span class="sourcing-supplier-meta">${escapeHtml(meta)}</span>` : ''}
            ${badge}
          </button>
        </div>`;
  }).join('');
  return rows || '<p class="sourcing-list-note">검색 결과 없음</p>';
}

export function renderSupplierModal(m: SupplierModalState): string {
  if (!m.open) return '';
  const body = m.loading
    ? `<p class="sourcing-list-note">불러오는 중…</p>`
    : m.all.length === 0
      ? `<p class="sourcing-list-note">활성 매입처가 없습니다.</p>`
      : `<div class="sourcing-supplier-list">${renderSupplierListInner(m)}</div>`;

  return `
      <div class="deal-quote-modal" role="dialog">
        <div class="deal-quote-modal-overlay" data-action="supplier-close"></div>
        <div class="deal-quote-modal-dialog sourcing-supplier-dialog">
          <header class="deal-quote-modal-header">
            <div>
              <h2>매입처 지정</h2>
              <p class="sourcing-supplier-subtitle">선택한 묶음(${escapeHtml(m.label)})에 연결할 매입처를 선택합니다.</p>
            </div>
            <button class="deal-rfq-modal-close" type="button" data-action="supplier-close" aria-label="닫기">✕</button>
          </header>
          <div class="deal-quote-modal-body sourcing-supplier-body">
            ${m.error ? `<div class="alert alert-error">${escapeHtml(m.error)}</div>` : ''}
            <input class="input sourcing-supplier-searchbox" type="text" data-action="supplier-search"
                   placeholder="매입처 검색" value="${escapeHtml(m.search)}">
            ${body}
          </div>
          <footer class="deal-quote-modal-footer">
            <div class="deal-quote-modal-actions">
              <button class="btn btn-primary sourcing-supplier-save" type="button" data-action="supplier-save" ${m.saving ? 'disabled' : ''}>${m.saving ? '저장 중…' : `저장${m.selected.length ? ` (${m.selected.length})` : ''}`}</button>
            </div>
          </footer>
        </div>
      </div>`;
}

// 개별 니즈 거래처 표시 설정(visibility) 모달 — 눈 아이콘으로 show/hide (마이그142).
export function renderVisibilityModal(m: VisibilityModalState): string {
  if (!m.open) return '';
  const q = m.search.trim().toLowerCase();
  // 행에 보이는 보조문구(품목 · 상태 · 가격)까지 검색 대상에 넣는다.
  //   `s.meta` 라는 필드는 없다 — 아래 렌더에서 조합해 만드는 값이라 같은 식을 쓴다.
  //   (예전엔 없는 필드를 참조해 검색이 항상 이름만 매칭했다)
  const metaOf = (s: VisibilityModalState['suppliers'][number]): string =>
    [m.item, s.status, s.price].filter(Boolean).join(' · ');
  const filtered = q
    ? m.suppliers.filter((s) =>
        s.supplier_name.toLowerCase().includes(q) || metaOf(s).toLowerCase().includes(q))
    : m.suppliers;
  const rows = filtered.map((s) => {
    // 이 매입처에 요청한 품목 + 현재 상태(+가격). row 전체가 토글 버튼.
    const meta = metaOf(s);
    return `
        <button class="sourcing-vis-row ${s.visible ? 'is-on' : ''}" type="button"
                data-action="visibility-toggle" data-supplier-id="${escapeHtml(s.supplier_id)}"
                title="${s.visible ? '표시 중 — 클릭해 숨김' : '숨김 — 클릭해 표시'}">
          <span class="sourcing-vis-eye ${s.visible ? 'is-on' : 'is-off'}" aria-hidden="true">👁</span>
          <div class="sourcing-vis-info">
            <div class="sourcing-vis-name">${escapeHtml(s.supplier_name)}${s.has_data ? ' <span class="sourcing-vis-dot" title="RFQ 데이터 있음(숨겨도 보존)">●</span>' : ''}</div>
            <div class="sourcing-vis-meta">${escapeHtml(meta) || '요청 대기'}</div>
          </div>
        </button>`;
  }).join('');
  const body = m.suppliers.length === 0
    ? `<p class="sourcing-list-note">그룹에 지정된 매입처가 없습니다. 먼저 ⚙ 로 그룹 매입처를 지정하세요.</p>`
    : filtered.length === 0
      ? `<p class="sourcing-list-note">검색 결과 없음</p>`
      : `<div class="sourcing-vis-list">${rows}</div>`;
  const visibleCount = m.suppliers.filter((s) => s.visible).length;
  return `
      <div class="deal-quote-modal" role="dialog">
        <div class="deal-quote-modal-overlay" data-action="visibility-close"></div>
        <div class="deal-quote-modal-dialog sourcing-supplier-dialog">
          <header class="deal-quote-modal-header">
            <div>
              <h2>거래처 표시 설정</h2>
              <p class="sourcing-supplier-subtitle">그룹에서 지정한 거래처의 표시 여부를 조정합니다. 눈 아이콘을 끄면 이 니즈에서만 잠시 보이지 않으며, 거래처와 RFQ 데이터는 삭제되지 않습니다.</p>
            </div>
            <button class="deal-rfq-modal-close" type="button" data-action="visibility-close" aria-label="닫기">✕</button>
          </header>
          <div class="deal-quote-modal-body sourcing-supplier-body">
            ${m.error ? `<div class="alert alert-error">${escapeHtml(m.error)}</div>` : ''}
            <input class="input sourcing-supplier-searchbox" type="text" data-action="visibility-search"
                   placeholder="매입처 검색" value="${escapeHtml(m.search)}">
            ${body}
          </div>
          <footer class="deal-quote-modal-footer">
            <div class="deal-quote-modal-actions">
              <button class="btn btn-primary" type="button" data-action="visibility-save" ${m.saving ? 'disabled' : ''}>${m.saving ? '저장 중…' : '거래처 표시 저장'}${visibleCount ? ` (${visibleCount})` : ''}</button>
              <button class="btn btn-secondary" type="button" data-action="visibility-close">취소</button>
            </div>
          </footer>
        </div>
      </div>`;
}
